import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { CloseButton } from 'pages/TimeClock/modals/base';
import { purgeGeoLocations, selectGeoLocationsPurging } from 'store/geoLocations';
import { styles } from './styles';

const useStyles = makeStyles(styles);

const AdminCredentialsDialog = ({ onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const purging = useSelector(selectGeoLocationsPurging);
  // Starts as `text` to dodge browser autofill, then flips to `password` on the first keystroke.
  const [passwordType, setPasswordType] = useState('text');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const {
    control,
    clearErrors,
    formState: { errors },
    getValues,
    setError,
  } = useForm({ defaultValues: { username: '', password: '' } });

  const handleSubmit = async () => {
    const { username, password } = getValues();

    if (!username.length) {
      setError('username', { type: 'error', message: '' });
      setError('password', { type: 'error', message: 'Username is required' });
      return;
    }

    if (!password.length) {
      setError('password', { type: 'error', message: 'Password is required' });
      return;
    }

    try {
      await dispatch(purgeGeoLocations({ username, password }));
      onClose();
    } catch (e) {
      setError('username', { type: 'error', message: '' });
      setError('password', { type: 'error', message: e?.response?.data?.message || e.message });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby="admin-credentials-title"
      PaperProps={{ classes: { root: classes.dialogRoot } }}
    >
      <CloseButton onClick={onClose} />
      <DialogContent style={{ paddingTop: '3rem' }}>
        <Box className={classes.fields}>
          <label htmlFor="username">
            <Typography variant="body1" color="textSecondary">
              Admin's Username
            </Typography>
            <Controller
              control={control}
              name="username"
              render={({ onChange, value }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  value={value}
                  onChange={(e) => {
                    clearErrors();
                    onChange(e.target.value);
                  }}
                  error={!!errors.username}
                  inputProps={{
                    'aria-label': "Admin's Username",
                    autoComplete: 'new-password',
                    'aria-autocomplete': 'none',
                  }}
                />
              )}
            />
            {errors.username ? <div className={classes.error}>{errors.username.message}</div> : null}
          </label>
          <label htmlFor="password">
            <Typography variant="body1" color="textSecondary">
              Admin's Password
            </Typography>
            <Controller
              control={control}
              name="password"
              render={({ onChange, value }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  value={value}
                  onChange={(e) => {
                    clearErrors();
                    setPasswordType('password');
                    onChange(e.target.value);
                  }}
                  error={!!errors.password}
                  type={passwordVisible ? 'text' : passwordType}
                  inputProps={{
                    'aria-label': "Admin's Password",
                    autoComplete: 'new-password',
                    'aria-autocomplete': 'none',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          component="button"
                          type="button"
                          variant="body2"
                          color="textSecondary"
                          className={classes.visibilityToggle}
                          onClick={() => setPasswordVisible((visible) => !visible)}
                        >
                          {passwordVisible ? 'Hide' : 'Show'}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            {errors.password ? <div className={classes.error}>{errors.password.message}</div> : null}
          </label>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          className={classes.confirmBtn}
          disabled={purging}
          disableTouchRipple
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminCredentialsDialog;
